// LeadsChart - Leads over time with Recharts
import { Paper, Title, Text, Stack } from '@mantine/core';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { LeadsOverTime } from '@/types/models/lead.types';

interface LeadsChartProps {
  data: LeadsOverTime[];
  isLoading?: boolean;
}

export const LeadsChart = ({ data, isLoading }: LeadsChartProps) => {
  if (isLoading || !data || data.length === 0) {
    return (
      <Paper withBorder p="md" radius="md" style={{ minHeight: 400 }}>
        <Stack align="center" justify="center" style={{ minHeight: 350 }}>
          <Text c="dimmed">
            {isLoading ? 'Loading chart data...' : 'No data available'}
          </Text>
        </Stack>
      </Paper>
    );
  }

  // Format data for chart
  const chartData = data.map((item) => ({
    date: new Date(item.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    'Total Leads': item.count,
    Converted: item.converted,
  }));

  return (
    <Paper withBorder p="md" radius="md">
      <Title order={3} mb="md">
        Leads Over Time (Last 30 Days)
      </Title>
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#2196f3" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorConverted" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="date" stroke="#666" />
          <YAxis stroke="#666" />
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
            }}
          />
          <Legend />
          <Area
            type="monotone"
            dataKey="Total Leads"
            stroke="#2196f3"
            fillOpacity={1}
            fill="url(#colorLeads)"
          />
          <Area
            type="monotone"
            dataKey="Converted"
            stroke="#4caf50"
            fillOpacity={1}
            fill="url(#colorConverted)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Paper>
  );
};
